import {
  AgxBadge,
  AgxButton,
  AgxDropdown,
  AgxModal,
  AgxSegmentPicker,
  AgxSpacer,
  AgxTypography,
  Icon_AlertCircleOutline,
  Icon_TargetOutline,
  useToast,
  type DropdownOption,
} from '@urbanx/agx.ui-components2';
import { useGetAllAgencies } from '@urbanx/agx.agencies.client';
import {
  SetLeaderboardVisibilityRequestEntityType,
  useGetLeaderboardVisibilityDetails,
  useInvalidateUseGetLeaderboardVisibilityDetails,
  useSetLeaderboardVisibility,
  type AgentLeaderboardVisibilitySummary,
} from '@urbanx/agx.support.client';
import LoadingState from 'components/loadingState/LoadingState';
import Panel from 'components/panel/Panel';
import { useCallback, useState } from 'react';

import SupportBackButton from '../SupportBackButton';

const getAgentName = (agent?: AgentLeaderboardVisibilitySummary) =>
  [agent?.firstName, agent?.lastName].filter((namePart) => !!namePart).join(' ') || 'Unnamed agent';

const getAgentLabel = (agent: AgentLeaderboardVisibilitySummary) => {
  const details = [agent.contactEmail, agent.softDeleted ? 'Soft deleted' : undefined]
    .filter((detail) => !!detail)
    .join(' · ');

  return details ? `${getAgentName(agent)} (${details})` : getAgentName(agent);
};

const getImpactCopy = (
  entityType: SetLeaderboardVisibilityRequestEntityType,
  excludeFromLeaderboards: boolean
) => {
  if (entityType === SetLeaderboardVisibilityRequestEntityType.Agency) {
    return excludeFromLeaderboards
      ? "This also removes the agency's agents and teams. It will not delete or disable them."
      : "This removes the agency's direct exclusion. Other leaderboard rules and agents or teams with their own exclusions still apply.";
  }

  if (entityType === SetLeaderboardVisibilityRequestEntityType.Team) {
    return excludeFromLeaderboards
      ? "This also removes the team's members from leaderboard calculations. It will not delete or disable them."
      : "This removes the team's direct exclusion. Agency and agent exclusions will still apply.";
  }

  return excludeFromLeaderboards
    ? 'This will not delete or disable the agent.'
    : "This removes the agent's direct exclusion. Agency and team exclusions can still keep this agent out of leaderboards.";
};

const entityTypeSegments = [
  { title: 'Agency', value: SetLeaderboardVisibilityRequestEntityType.Agency },
  { title: 'Team', value: SetLeaderboardVisibilityRequestEntityType.Team },
  { title: 'Agent', value: SetLeaderboardVisibilityRequestEntityType.Agent },
];

const LeaderboardVisibility = () => {
  const { pushToast } = useToast();
  const [entityType, setEntityType] = useState(SetLeaderboardVisibilityRequestEntityType.Agency);
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [selectedChildEntityId, setSelectedChildEntityId] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const {
    data: agencies,
    isPending: isLoadingAgencies,
    isError: hasAgenciesError,
  } = useGetAllAgencies();
  const {
    data: visibilityDetails,
    isPending: isLoadingVisibility,
    isError: hasVisibilityError,
  } = useGetLeaderboardVisibilityDetails(selectedAgencyId, !!selectedAgencyId);
  const invalidateVisibilityDetails = useInvalidateUseGetLeaderboardVisibilityDetails();

  const { setLeaderboardVisibility, setLeaderboardVisibilityPending } = useSetLeaderboardVisibility(
    {
      onSuccess: async (_returnData, inputData) => {
        setShowConfirmModal(false);
        await invalidateVisibilityDetails(selectedAgencyId);

        pushToast({
          message: `${inputData.entityType} ${inputData.excludeFromLeaderboards ? 'excluded from' : 'included in'} leaderboards`,
          type: 'success',
        });
      },
      onError: () => {
        pushToast({
          message: 'Unable to update leaderboard visibility',
          type: 'error',
        });
      },
    }
  );

  const agencyOptions: DropdownOption[] = (agencies ?? [])
    .filter((agency) => !!agency.id)
    .map((agency) => ({
      label: agency.name ?? 'Unnamed agency',
      value: agency.id!,
      searchText: agency.name ?? '',
    }));

  const agentOptions: DropdownOption[] = (visibilityDetails?.agents ?? [])
    .filter((agent) => !!agent.id)
    .map((agent) => ({
      label: getAgentLabel(agent),
      value: agent.id!,
      searchText: `${getAgentName(agent)} ${agent.contactEmail ?? ''}`,
    }));

  const teamOptions: DropdownOption[] = (visibilityDetails?.teams ?? [])
    .filter((team) => !!team.id)
    .map((team) => ({
      label: team.name ?? 'Unnamed team',
      value: team.id!,
      searchText: team.name ?? '',
    }));

  const selectedAgent = visibilityDetails?.agents?.find(
    (agent) => agent.id === selectedChildEntityId
  );
  const selectedTeam = visibilityDetails?.teams?.find((team) => team.id === selectedChildEntityId);
  const selectedEntityDetails = {
    [SetLeaderboardVisibilityRequestEntityType.Agency]: {
      entity: visibilityDetails?.agency,
      name: visibilityDetails?.agency?.name,
    },
    [SetLeaderboardVisibilityRequestEntityType.Team]: {
      entity: selectedTeam,
      name: selectedTeam?.name,
    },
    [SetLeaderboardVisibilityRequestEntityType.Agent]: {
      entity: selectedAgent,
      name: selectedAgent ? getAgentName(selectedAgent) : undefined,
    },
  }[entityType];
  const { entity: selectedEntity, name: selectedEntityName } = selectedEntityDetails;
  const entityLabel = entityType.toLowerCase();
  const excludeFromLeaderboards = !selectedEntity?.excludeFromLeaderboards;
  const action = excludeFromLeaderboards ? 'Exclude' : 'Include';
  const canUpdate = !!selectedEntity?.id && !setLeaderboardVisibilityPending;

  const onEntityTypeChange = useCallback((value: string) => {
    setEntityType(value as SetLeaderboardVisibilityRequestEntityType);
    setSelectedChildEntityId('');
  }, []);

  const onAgencyChange = (agencyId: string) => {
    setSelectedAgencyId(agencyId);
    setEntityType(SetLeaderboardVisibilityRequestEntityType.Agency);
    setSelectedChildEntityId('');
  };

  const onConfirm = async () => {
    if (!selectedEntity?.id) return;

    await setLeaderboardVisibility({
      entityType,
      entityId: selectedEntity.id,
      excludeFromLeaderboards,
    });
  };

  return (
    <AgxSpacer direction="vertical" gap="spacing-xl" fill>
      <SupportBackButton />

      <Panel title="Leaderboard Visibility">
        <AgxSpacer direction="vertical" gap="spacing-xl" fill>
          <AgxTypography id="leaderboard-visibility-instructions" type="body" size="small">
            Select an agency, then choose whether to update the agency itself, one of its teams, or
            one of its agents.
          </AgxTypography>

          <AgxDropdown
            id="leaderboard-visibility-agency"
            label="Agency"
            selected={selectedAgencyId}
            options={agencyOptions}
            onChange={onAgencyChange}
            placeholder="Select agency"
            searchable
            searchPlaceholder="Search agencies"
            stretch
            hideOptionalText
            disabled={isLoadingAgencies || setLeaderboardVisibilityPending}
          />

          {(isLoadingAgencies || (!!selectedAgencyId && isLoadingVisibility)) && <LoadingState />}

          {(hasAgenciesError || hasVisibilityError) && (
            <AgxTypography id="leaderboard-visibility-error" type="body" size="small">
              Unable to load leaderboard visibility.
            </AgxTypography>
          )}

          {selectedAgencyId && !isLoadingVisibility && !hasVisibilityError && visibilityDetails && (
            <>
              <AgxSegmentPicker
                key={selectedAgencyId}
                id="leaderboard-visibility-entity-type"
                label="Update leaderboard visibility for"
                segments={entityTypeSegments}
                defaultValue={SetLeaderboardVisibilityRequestEntityType.Agency}
                size="medium"
                onChange={onEntityTypeChange}
              />

              <AgxSpacer direction="vertical" gap="spacing-lg" fill contentAlignment="start">
                {entityType === SetLeaderboardVisibilityRequestEntityType.Agent && (
                  <AgxDropdown
                    id="leaderboard-visibility-agent"
                    label="Agent"
                    selected={selectedChildEntityId}
                    options={agentOptions}
                    onChange={setSelectedChildEntityId}
                    placeholder="Select agent"
                    searchable
                    searchPlaceholder="Search agents"
                    stretch
                    hideOptionalText
                    disabled={setLeaderboardVisibilityPending}
                  />
                )}

                {entityType === SetLeaderboardVisibilityRequestEntityType.Team && (
                  <AgxDropdown
                    id="leaderboard-visibility-team"
                    label="Team"
                    selected={selectedChildEntityId}
                    options={teamOptions}
                    onChange={setSelectedChildEntityId}
                    placeholder="Select team"
                    searchable
                    searchPlaceholder="Search teams"
                    stretch
                    hideOptionalText
                    disabled={setLeaderboardVisibilityPending}
                  />
                )}

                {selectedEntity && (
                  <AgxButton
                    id="leaderboard-visibility-submit"
                    text={`${action} ${entityLabel}`}
                    variant="primary"
                    leftIcon={<Icon_TargetOutline width={16} height={16} />}
                    disabled={!canUpdate}
                    onClick={() => setShowConfirmModal(true)}
                  />
                )}
              </AgxSpacer>

              {entityType === SetLeaderboardVisibilityRequestEntityType.Team &&
                teamOptions.length === 0 && (
                  <AgxTypography id="leaderboard-visibility-teams-empty" type="body" size="small">
                    No teams were found for this agency.
                  </AgxTypography>
                )}

              {selectedEntity && (
                <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
                  <AgxTypography
                    id="leaderboard-visibility-status-label"
                    type="body_ui_strong"
                    size="small"
                  >
                    Current status
                  </AgxTypography>
                  <AgxBadge
                    type="colourfill"
                    size="sm"
                    colour={selectedEntity.excludeFromLeaderboards ? 'grey' : 'green'}
                    text={
                      selectedEntity.excludeFromLeaderboards
                        ? 'Excluded from leaderboards'
                        : 'Included in leaderboards'
                    }
                  />
                </AgxSpacer>
              )}
            </>
          )}
        </AgxSpacer>
      </Panel>

      <AgxModal
        id="leaderboard-visibility-confirm-modal"
        title={`Confirm ${action.toLowerCase()}`}
        titleIcon={<Icon_AlertCircleOutline type="Danger" height={40} width={40} />}
        type="single"
        variant="withDivider"
        actionButtonContainerStyle="withDivider"
        isOpen={showConfirmModal}
        setIsDialogOpen={setShowConfirmModal}
        actionButtons={
          <AgxSpacer direction="horizontal" gap="spacing-md" contentJustification="center" fill>
            <AgxButton
              id="leaderboard-visibility-cancel"
              text="Cancel"
              variant="hollow"
              stretch
              disabled={setLeaderboardVisibilityPending}
              onClick={() => setShowConfirmModal(false)}
            />
            <AgxButton
              id="leaderboard-visibility-confirm"
              text={setLeaderboardVisibilityPending ? 'Updating' : action}
              variant="primary"
              stretch
              disabled={setLeaderboardVisibilityPending}
              onClick={() => void onConfirm()}
            />
          </AgxSpacer>
        }
        content={
          <AgxSpacer direction="vertical" gap="spacing-md" padding="spacing-4xl">
            <AgxTypography id="leaderboard-visibility-confirm-copy" type="body" size="medium">
              {action} {selectedEntityName ?? `the selected ${entityLabel}`}{' '}
              {excludeFromLeaderboards ? 'from' : 'in'} all Top Performers leaderboards?
            </AgxTypography>
            <AgxTypography id="leaderboard-visibility-impact-copy" type="body" size="small">
              {getImpactCopy(entityType, excludeFromLeaderboards)}
            </AgxTypography>
          </AgxSpacer>
        }
      />
    </AgxSpacer>
  );
};

export default LeaderboardVisibility;
